import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { ExternalLink } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";

interface PrivacyPolicyDialogProps {
  trigger?: React.ReactNode;
  children?: React.ReactNode;
}

const PrivacyPolicyDialog = ({ trigger, children }: PrivacyPolicyDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || children || (
          <a className="p-0 h-auto text-sm hover:underline">
            Privacy Policy
          </a>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Privacy Policy</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 h-full bg-background p-4 rounded-lg">
          <div className="space-y-4 text-base">
            <div>
              <p className="leading-relaxed">
                Orbius Communications ehf ("Orbius", "we", "us", or "our") is committed to protecting your
                privacy and ensuring the responsible handling of your personal information. This Privacy
                Policy explains how we collect, use, store, and share personal data in compliance with the
                General Data Protection Regulation (GDPR) and other applicable data protection laws.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Who We Are</h3>
              <div className="space-y-2 mt-3">
                <p>
                  <span>📍 <strong>Orbius Communications ehf</strong></span><br />
                  <span>Olafsgeisli 15</span><br />
                  <span>113 Reykjavík, Iceland</span>
                </p>
                <p>
                  <strong>Email: </strong>
                  <a
                    href="mailto:support@orbius.com"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    support@orbius.com
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
                <p className="mt-2">
                  We are established in Iceland, which is part of the European Economic Area (EEA).
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold">What Data We Collect</h3>
              <p className="mt-3">
                For our direct customers, we may collect and process the following business-related
                personal data:
              </p>
              <ul className="space-y-2 list-disc list-inside mt-3">
                <li>Full name</li>
                <li>Email address</li>
                <li>Company or organization name</li>
                <li>Enterprise delivery address</li>
                <li>Service line location</li>
                <li>Activity within our customer portal</li>
                <li>Records of customer support interactions</li>
                <li>Any additional information voluntarily provided by the customer</li>
              </ul>
              <p className="mt-3">
                <strong>We do not collect sensitive personal data</strong> (such as health, biometric, or political
                information).
              </p>
              <p className="mt-3">
                We have no visibility into, or knowledge of, individual end users of the services. Unless a
                customer explicitly provides such details, we cannot identify end users, and apart from
                recording the service line location, we do not process personal data relating to them.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">How We Use Your Data & Legal Basis</h3>
              <p className="mt-3">
                We only use your data for legitimate, lawful purposes. The table below shows the purpose
                and legal basis for each processing activity:
              </p>
              <div className="mt-4 overflow-x-auto">
                <Table className="w-full border-collapse border border-gray-500 text-xs">
                  <TableHeader className="bg-muted">
                    <TableRow className="border-b">
                      <TableHead className="border border-gray-500 p-2 text-left font-semibold">Purpose</TableHead>
                      <TableHead className="border border-gray-500 p-2 text-left font-semibold">Legal Basis (GDPR)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="border border-gray-500 p-2">Respond to inquiries</TableCell>
                      <TableCell className="border border-gray-500 p-2">Legitimate interests (Article 6(1)(f))</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="border border-gray-500 p-2">Fulfill contracts, process and ship orders</TableCell>
                      <TableCell className="border border-gray-500 p-2">Contractual necessity (Article 6(1)(b))</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="border border-gray-500 p-2">Provide customer support</TableCell>
                      <TableCell className="border border-gray-500 p-2">Contractual necessity (Article 6(1)(b))</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="border border-gray-500 p-2">Maintain and improve portal security and functionality</TableCell>
                      <TableCell className="border border-gray-500 p-2">Legitimate interests (Article 6(1)(f))</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="border border-gray-500 p-2">Comply with tax, legal, and regulatory obligations</TableCell>
                      <TableCell className="border border-gray-500 p-2">Legal obligation (Article 6(1)(c))</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="border border-gray-500 p-2">Send relevant updates and marketing</TableCell>
                      <TableCell className="border border-gray-500 p-2">Consent (Article 6(1)(a))</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <p className="mt-3">
                We do not sell your data or use it for third-party marketing.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">How We Store and Protect Your Data</h3>
              <ul className="space-y-2 list-disc list-inside mt-3">
                <li>All data is stored on secure servers protected by firewalls, encryption, and access controls.</li>
                <li>Access is restricted to authorized personnel.</li>
                <li>We monitor systems for vulnerabilities.</li>
              </ul>
              <p className="mt-3">
                While we take robust measures, no system is 100% secure, and transmission over the
                internet is never fully risk-free.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Data Sharing</h3>
              <p className="mt-3">
                We may share your data with trusted third-party providers such as:
              </p>
              <ul className="space-y-2 list-disc list-inside mt-3">
                <li>Logistics and shipping partners</li>
                <li>CRM and communication tools</li>
                <li>Legal and regulatory bodies</li>
              </ul>
              <p className="mt-3">
                These parties act as data processors under confidentiality and security obligations and only
                process data on our instructions.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">International Transfers</h3>
              <p className="mt-3">
                If your personal data is transferred outside the EEA, we ensure appropriate safeguards are
                in place, such as:
              </p>
              <ul className="space-y-2 list-disc list-inside mt-3">
                <li>Adequacy decisions issued by the European Commission, or</li>
                <li>Standard Contractual Clauses (SCCs) approved by the European Commission.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Data Retention</h3>
              <p className="mt-3">
                We retain personal data only for as long as necessary to fulfill the purposes outlined in this
                policy:
              </p>
              <ul className="space-y-2 list-disc list-inside mt-3">
                <li><strong>Customer account data:</strong> for the duration of the contract and up to 6 years after (to meet legal and tax requirements).</li>
                <li><strong>Support records:</strong> typically deleted after 12 months unless required for ongoing issues.</li>
                <li><strong>Marketing communications:</strong> until you withdraw consent.</li>
              </ul>
              <p className="mt-3">
                We routinely review and delete unnecessary data.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Your Rights</h3>
              <p className="mt-3">
                Under GDPR, you may exercise the following rights at any time:
              </p>
              <ul className="space-y-2 list-disc list-inside mt-3">
                <li><strong>Access</strong> – obtain a copy of your personal data</li>
                <li><strong>Rectification</strong> – correct inaccurate data</li>
                <li><strong>Erasure</strong> – request deletion where legally possible</li>
                <li><strong>Restriction</strong> – limit certain processing</li>
                <li><strong>Objection</strong> – object to processing based on legitimate interests</li>
                <li><strong>Portability</strong> – request data in a structured, machine-readable format</li>
              </ul>
              <p className="mt-3">
                To exercise your rights, email us at
                <a
                  href="mailto:support@orbius.com"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  support@orbius.com
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
              <p className="mt-3">
                You also have the right to lodge a complaint with the Icelandic Data Protection Authority
                (Persónuvernd) or your local supervisory authority.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Automated Decision-Making</h3>
              <p className="mt-3">
                We do not use your personal data for automated decision-making or profiling that produces
                legal or similarly significant effects.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Children's Data</h3>
              <p className="mt-3">
                Our services are intended for business use only and are not directed to children under 16.
                We do not knowingly collect personal data from children.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Cookies & Tracking</h3>
              <p className="mt-3">
                We do not currently use cookies, analytics scripts, or third-party trackers.
              </p>
              <p>
                If this changes, we will update this policy and request consent where required (e.g., via
                cookie banners).
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Third-Party Links</h3>
              <p className="mt-3">
                Our website may link to third-party services. We are not responsible for the privacy
                practices of those websites. We encourage you to review their privacy policies before
                sharing data with them.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Changes to this Policy</h3>
              <p className="mt-3">
                We may update this Privacy Policy from time to time.
              </p>
              <ul className="space-y-2 list-disc list-inside mt-3">
                <li>Minor updates will be published on this page with a revised effective date.</li>
                <li>If we make significant changes, we will notify you directly (e.g., by email or via our portal).</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold">Contact Us</h3>
              <p className="mt-3">
                If you have questions or concerns about this Privacy Policy or how we handle your data,
                please contact:
              </p>
              <p className="mt-3">
                <a
                  href="mailto:support@orbius.com"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  support@orbius.com
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
              <p className="mt-3">
                <span>📍 <strong>Orbius Communications ehf</strong>, Olafsgeisli 15, 113 Reykjavík, Iceland</span>
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyDialog; 